<template>
  <div>
    <v-row>
      <v-col class="py-2">
        <FlyoutMenuHead
            :selectedItem="getSelectedDevice"
            type="edit"
            route="devices"
        />
        <v-divider></v-divider>
        <CustomForm
            :formData="formData"
            type="edit"
            @onAddTag="addNewTag"
            :currentData="getSelectedDevice"
            @onSubmit="editDevice"
            :loading="loader"
            :forceChanged="customFieldsChanged"
            @onClose="
            () => {
              this.$router.go(-1);
            }
          ">
          <!-- Custom Fields Section -->
          <v-row v-if="hasEnabledDeviceCustomFields" class="px-4 mt-2">
            <v-col md="8" class="pl-0">
              <div class="pb-4 pl-0">
                <span class="subtitle text-secondary">{{ $t('assets.tabs.custom_fields') }}</span>
              </div>
              <template v-for="n in 8">
                <v-col
                  v-if="deviceForm[`device_custom_${n}_enabled`]"
                  :key="n"
                  cols="12"
                  class="px-0 py-1"
                >
                  <v-text-field
                    v-model="deviceForm[`device_custom_${n}`]"
                    :label="deviceForm[`device_custom_${n}_alias`]"
                    :error-messages="fieldErrors[`device_custom_${n}`] || []"
                    @input="$set(fieldErrors, `device_custom_${n}`, [])"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </template>
            </v-col>
          </v-row>
        </CustomForm>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import DevicesApi from "@/services/api/Devices";
import CustomForm from "@/components/CustomForm";
import FlyoutMenuHead from "@/components/FlyoutMenuHead";
import {mapActions, mapGetters} from "vuex";

export default {
  name: "DeviceInfoEdit",
  components: {
    FlyoutMenuHead,
    CustomForm,
  },
  mounted() {
    const fetchDevice = !this.getSelectedDevice.uuid
      ? this.getDevice({ route: this.$route.name, uuid: this.$route.params.uuid })
      : Promise.resolve();
    fetchDevice.then(() => {
      this.getSpaceDeviceCustomFields().then(() => {
        const updated = {};
        this.getDeviceCustomFields.forEach(f => {
          updated[`device_custom_${f.index}_enabled`] = f.enabled;
          updated[`device_custom_${f.index}_required`] = f.required;
          updated[`device_custom_${f.index}_alias`] = f.alias || `Custom ${f.index}`;
          updated[`device_custom_${f.index}`] = this.getSelectedDevice[`device_custom_${f.index}`] || '';
        });
        this.deviceForm = updated;
        this.originalDeviceForm = { ...updated };
      });
    });
  },
  methods: {
    ...mapActions({"getDevice": "devices/getSpecificDevice", "getTags": "tags/getTags", "addTag": "tags/addTag", "getSpaceDeviceCustomFields": "devices/getSpaceDeviceCustomFields"}),
    addNewTag(tag) {
      this.addTag(tag);
    },
    editDevice(data) {
      this.fieldErrors = {};
      let hasErrors = false;
      for (let i = 1; i <= 8; i++) {
        if (this.deviceForm[`device_custom_${i}_enabled`] && this.deviceForm[`device_custom_${i}_required`] && !this.deviceForm[`device_custom_${i}`]) {
          const alias = this.deviceForm[`device_custom_${i}_alias`] || `Custom Field ${i}`;
          this.$set(this.fieldErrors, `device_custom_${i}`, [`${alias} is required.`]);
          hasErrors = true;
        }
      }
      if (hasErrors) return;

      this.loader = true;
      const customData = {};
      for (let i = 1; i <= 8; i++) {
        if (this.deviceForm[`device_custom_${i}_enabled`]) {
          customData[`device_custom_${i}`] = this.deviceForm[`device_custom_${i}`];
        }
      }
      DevicesApi.editDevice(
          this.getSelectedDevice.space_uuid,
          this.getSelectedDevice.uuid,
          { ...data, ...customData },
      )
          .then((response) => {
            if (response.status === 200) {
              let payload = {
                "uuid": this.getSelectedDevice.uuid,
                "data": response.data
              };
              this.$store.commit("devices/UPDATE_DEVICE_UUID", payload);
              this.$store.commit("updateAlert", {
                type: "success",
                shown: true,
                message: `Device ${this.getSelectedDevice.name} updated successfully.`,
              });
              this.$router.go(-1);
            }
          }).catch((err) => {
        if (err.response && err.response.status === 409) {
          const message = err.response.data?.message;

          if (message) {
            const match = message.match(/device_custom_(\d+)/);

            if (match) {
              const field = match[0];

              this.$set(this.fieldErrors, field, [message]);
              return;
            }
          }
        }

        if (err.response && err.response.status === 400) {
          const errData = err.response.data;

          Object.keys(errData).forEach(field => {
            const messages = Array.isArray(errData[field])
              ? errData[field]
              : [String(errData[field])];

            this.$set(this.fieldErrors, field, messages);
          });

          return;
        }

        this.$store.commit('updateAlert', {
          type: 'error',
          shown: true,
          message: 'Failed to add device.'
        });
      });
    },
  },
  watch: {
    deviceForm: {
      deep: true,
      handler(newVal) {
        if (!this.originalDeviceForm) return;
        this.customFieldsChanged = Object.keys(this.originalDeviceForm).some(key => {
          if (key.endsWith('_enabled') || key.endsWith('_required') || key.endsWith('_alias')) return false;
          return newVal[key] !== this.originalDeviceForm[key];
        });
      }
    }
  },
  data: () => ({
    loader: false,
    deviceForm: {},
    fieldErrors: {},
    customFieldsChanged: false,
    originalDeviceForm: null,
    formData: {
      sections: [
        {
          title: "General",
          inputs: {
            name: {
              value: "name",
              gridSize: 8,
              type: "text",
              placeholder: "Friendly name for your device",
              label: "Device Name",
            },
            description: {
              value: "description",
              gridSize: 8,
              type: "textarea",
              placeholder: "Description",
              options: {
                counter: 1000,
                hint: "You can always edit predefined description",
              },
            },
          },
        },
        {
          title: "Tags",
          inputs: {
            tag_uuids: {
              value: "tag_uuids",
              gridSize: 8,
              type: "tags",
              placeholder: "Edit tags for your device",
              label: "Tags",
              options: {
                items: [],
                action: "tags/getTags",
                actionPayload: "device",
                counter: 20,
                text: "name",
                value: "uuid"
              }
            }
          }
        }
      ],
    },
  }),
  computed: {
    ...mapGetters("devices", ["getSelectedDevice", "getDeviceCustomFields"]),
    hasEnabledDeviceCustomFields() {
      return Array.isArray(this.getDeviceCustomFields) && this.getDeviceCustomFields.some(f => f.enabled);
    },
  },
};
</script>

<style scoped lang="scss"></style>
